/**
 * The live chunk size control.
 *
 * BUD-17's `chunk_size` and `max_links` are writer-side constants: a reader just
 * follows `h` and `s` down the links and concatenates, so it never needs to know
 * what size the writer chose. That means the user can move this slider freely
 * and the resulting tree still resolves in any conformant client. What a
 * non-canonical size *does* cost is dedup against clients using the canonical
 * 2 MiB, because the same file chunked differently hashes differently.
 *
 * Showing the split update live is the fastest way to make all of that obvious.
 */

import { CANONICAL_CHUNK_SIZE, CANONICAL_MAX_LINKS, type FileGeometry } from "@/hashtree/nodes";
import { formatBytes } from "@/hashtree/bytes";
import { CHUNK_PRESETS, MAX_CHUNK_EXPONENT, MIN_CHUNK_EXPONENT } from "@/hashtree/useHashtreeBuild";
import { cn } from "@/lib/utils";
import { Prop } from "./Prop";

function nearestExponent(value: number): number {
  return Math.round(Math.log2(value));
}

interface ChunkSizeControlProps {
  chunkSize: number;
  onChunkSize: (size: number) => void;
  maxLinks: number;
  onMaxLinks: (count: number) => void;
  geometry: FileGeometry | null;
}

export function ChunkSizeControl({
  chunkSize,
  onChunkSize,
  maxLinks,
  onMaxLinks,
  geometry,
}: ChunkSizeControlProps) {
  const exponent = nearestExponent(chunkSize);
  const isCanonical = chunkSize === CANONICAL_CHUNK_SIZE && maxLinks === CANONICAL_MAX_LINKS;

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <label htmlFor="chunk-size" className="text-sm font-medium">
            chunk_size
          </label>
          <p className="text-xs text-muted-foreground">Maximum plaintext bytes in each leaf chunk</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-lg tabular-nums">{formatBytes(chunkSize)}</span>
          {chunkSize === CANONICAL_CHUNK_SIZE && (
            <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[0.65rem] font-medium text-emerald-700 dark:text-emerald-400">
              canonical
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <input
          id="chunk-size"
          type="range"
          min={MIN_CHUNK_EXPONENT}
          max={MAX_CHUNK_EXPONENT}
          step={1}
          value={exponent}
          onChange={(event) => onChunkSize(2 ** Number(event.target.value))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-[0.65rem] text-muted-foreground">
          <span>64 B</span>
          <span>2 MiB (canonical)</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {CHUNK_PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            onClick={() => onChunkSize(preset.value)}
            className={cn(
              "rounded-md border px-2 py-1 text-xs transition-colors",
              chunkSize === preset.value ? "border-primary bg-primary/10 font-medium" : "hover:bg-muted",
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
        <div>
          <label htmlFor="max-links" className="text-sm font-medium">
            max_links
          </label>
          <p className="text-xs text-muted-foreground">
            Links per node before manifests nest. Lower it to force nesting on a small file.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="max-links"
            type="number"
            min={2}
            max={1024}
            value={maxLinks}
            onChange={(event) => onMaxLinks(Math.max(2, Number(event.target.value) || 2))}
            className="w-24 rounded-md border bg-background px-2 py-1 font-mono text-sm tabular-nums"
          />
          {maxLinks !== CANONICAL_MAX_LINKS && (
            <button
              type="button"
              onClick={() => onMaxLinks(CANONICAL_MAX_LINKS)}
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              reset to 174
            </button>
          )}
        </div>
      </div>

      {geometry && <ChunkPreview geometry={geometry} />}

      <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
        {isCanonical ? (
          <>
            These are the canonical BUD-17 constants, so this tree dedups against any other client that uses them.
          </>
        ) : (
          <>
            Any size still resolves in any conformant client — readers never look at <code>chunk_size</code>. The only
            cost is dedup: the same file chunked at {formatBytes(chunkSize)} hashes differently than at 2 MiB.
          </>
        )}
      </p>
    </div>
  );
}

/** Live preview of how the file splits and what shape the tree takes. */
export function ChunkPreview({ geometry }: { geometry: FileGeometry }) {
  const { chunks, shape, blobCount, manifestBytes, summary } = geometry;
  const shown = chunks.slice(0, 24);

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="font-medium">{summary}</span>
        <span className="text-muted-foreground">
          {blobCount} blob{blobCount === 1 ? "" : "s"} to upload
        </span>
        {manifestBytes > 0 && <span className="text-muted-foreground">{manifestBytes} B of manifests</span>}
      </div>

      {/* Proportional bar: each segment is one chunk, the short remainder included. */}
      <div className="flex h-7 w-full gap-px overflow-hidden rounded-md border bg-muted/40">
        {shown.map((chunk) => (
          <div
            key={chunk.index}
            title={`chunk ${chunk.index}: bytes ${chunk.start}–${chunk.end - 1} (${chunk.size} B)`}
            className={cn(
              "flex min-w-0 items-center justify-center bg-primary/25 text-[0.6rem] tabular-nums",
              chunk.size < geometry.chunkSize && "bg-amber-500/30",
            )}
            style={{ flexGrow: Math.max(chunk.size, 1), flexBasis: 0 }}
          >
            {shown.length <= 12 ? chunk.index : ""}
          </div>
        ))}
        {chunks.length > shown.length && (
          <div className="flex shrink-0 items-center px-2 text-[0.6rem] text-muted-foreground">
            +{chunks.length - shown.length} more
          </div>
        )}
      </div>

      {chunks.length > 1 && (
        <p className="text-xs text-muted-foreground">
          The last chunk is short ({chunks[chunks.length - 1]!.size} B) — chunks are filled in order, so only the tail
          is a remainder.
        </p>
      )}

      <ShapeDiagram shape={shape} />
    </div>
  );
}

/** The three branches of BUD-17's splitting algorithm, as the user slides between them. */
function ShapeDiagram({ shape }: { shape: FileGeometry["shape"] }) {
  if (shape.kind === "single-blob") {
    return (
      <ShapeNote tone="emerald" title="No file manifest">
        The file fits in one chunk, so BUD-17 lets you publish it as a plain <Prop name="t" value={0} /> blob. The folder links
        straight to it.
      </ShapeNote>
    );
  }

  if (shape.kind === "flat-manifest") {
    return (
      <ShapeNote tone="violet" title="One flat t=1 file manifest">
        {shape.chunkCount} chunk links fit in a single manifest. The folder links to the manifest, and the manifest
        links to the chunks.
      </ShapeNote>
    );
  }

  return (
    <ShapeNote tone="orange" title={`Nested t=1 manifests, ${shape.depth} levels deep`}>
      {shape.chunkCount} chunks exceed max_links, so they group into {shape.manifestCount} manifests. Parent links use{" "}
      <Prop name="t" value={1} /> with <Prop name="s" /> set to the sum of their descendants.
    </ShapeNote>
  );
}

const TONES = {
  emerald: "border-emerald-500/30 bg-emerald-500/10",
  violet: "border-violet-500/30 bg-violet-500/10",
  orange: "border-orange-500/30 bg-orange-500/10",
} as const;

function ShapeNote({
  tone,
  title,
  children,
}: {
  tone: keyof typeof TONES;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-md border px-3 py-2", TONES[tone])}>
      <p className="text-xs font-medium">{title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{children}</p>
    </div>
  );
}
